/**
 * IK Gizmo component for interactive IK control
 * Uses PivotControls from @react-three/drei for both position and rotation control
 */

import { useRef, useEffect, useCallback, useState, memo } from "react";
import { PivotControls } from "@react-three/drei";
import * as THREE from "three";
import type { IKGizmoData } from "../types";
import { useWebSocket } from "../WebSocket";
import { useIKPositionMask, useIKRotationMask } from "../store";

interface IKGizmoProps {
  gizmo: IKGizmoData;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

export const IKGizmo = memo(function IKGizmo({ gizmo, onDragStart, onDragEnd }: IKGizmoProps) {
  const { sendIKGizmoUpdate } = useWebSocket();
  const isDraggingRef = useRef(false);
  const positionMask = useIKPositionMask();
  const rotationMask = useIKRotationMask();

  // Store current position/rotation as state for PivotControls matrix
  const [matrix, setMatrix] = useState<THREE.Matrix4>(() => {
    const m = new THREE.Matrix4();
    const pos = new THREE.Vector3(...gizmo.position);
    const [x, y, z, w] = gizmo.quaternion;
    const quat = new THREE.Quaternion(x, y, z, w);
    m.compose(pos, quat, new THREE.Vector3(1, 1, 1));
    return m;
  });

  // Update matrix from gizmo data (when backend updates position after IK solve)
  useEffect(() => {
    // Only update if not currently dragging
    if (isDraggingRef.current) return;

    const m = new THREE.Matrix4();
    const pos = new THREE.Vector3(...gizmo.position);
    const [x, y, z, w] = gizmo.quaternion;
    const quat = new THREE.Quaternion(x, y, z, w);
    m.compose(pos, quat, new THREE.Vector3(1, 1, 1));
    setMatrix(m);
  }, [gizmo.position, gizmo.quaternion]);

  // Handle drag - send to backend
  const handleDrag = useCallback(
    (localMatrix: THREE.Matrix4) => {
      if (!isDraggingRef.current) return;

      const position = new THREE.Vector3();
      const quaternion = new THREE.Quaternion();
      const scale = new THREE.Vector3();
      localMatrix.decompose(position, quaternion, scale);

      const posArray: [number, number, number] = [
        position.x,
        position.y,
        position.z,
      ];
      const quatArray: [number, number, number, number] = [
        quaternion.x,
        quaternion.y,
        quaternion.z,
        quaternion.w,
      ];

      sendIKGizmoUpdate(gizmo.gizmo_id, posArray, quatArray);
    },
    [gizmo.gizmo_id, sendIKGizmoUpdate]
  );

  // Determine active axes based on position mask
  const activeAxes: [boolean, boolean, boolean] = [
    positionMask[0],
    positionMask[1],
    positionMask[2],
  ];

  // Determine if rotations are disabled
  const disableRotations =
    !rotationMask[0] && !rotationMask[1] && !rotationMask[2];

  return (
    <PivotControls
      matrix={matrix}
      scale={0.15}
      lineWidth={2}
      fixed={false}
      activeAxes={activeAxes}
      disableAxes={false}
      disableSliders={true}
      disableRotations={disableRotations}
      disableScaling={true}
      depthTest={false}
      opacity={0.8}
      onDragStart={() => {
        isDraggingRef.current = true;
        onDragStart?.();
      }}
      onDrag={(localMatrix) => {
        handleDrag(localMatrix);
      }}
      onDragEnd={() => {
        isDraggingRef.current = false;
        onDragEnd?.();
      }}
    />
  );
});
